import React, { useMemo } from "react";
import { useParaplanningCases } from "../../../Paraplanning/hooks/useParaplanningCases";
import { formatCurrency } from "../../../Paraplanning/utils/formatCurrency";
import "../../../../css/Praplanning/ParaplanningDashboard.css";
import "../../../../css/Praplanning/ParaplanningCasesTable.css";

const normaliseEmail = (email = "") => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

const formatDate = (value, options = {}) => {
  if (!value) return "-";

  const date = value?.toDate ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB", {
    weekday: options.showDay ? "short" : undefined,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getAmountCharged = (caseItem) => {
  if (caseItem.status !== "completed") return "-";

  const value = caseItem.costs?.totalCost;

  if (value === undefined || value === null || value === "") return "-";

  return formatCurrency(value);
};

const ParaplanningAdviserTab = ({ adviserEmail }) => {
  // Fetch all cases with admin permissions to see adviser's cases
  const queryFilters = useMemo(
    () => ({
      catalogId: "",
      search: "",
      status: "all",
      startDate: "",
      monthKey: "",
      quarterKey: "",
      showArchived: false,
      userEmail: "",
      userName: "",
      isAdmin: true,
      canManageAllCases: true,
      assignedTo: "",
    }),
    [],
  );

  const { cases: allCases, loadingCases } =
    useParaplanningCases(queryFilters);

  // Filter cases assigned to this adviser
  const filteredCases = useMemo(() => {
    const normalisedAdviserEmail = normaliseEmail(adviserEmail);
    return (allCases || []).filter((caseItem) => {
      const assignedEmail = normaliseEmail(
        caseItem?.adviser?.email ||
          caseItem?.assignedToEmail ||
          caseItem?.assignedTo?.value ||
          "",
      );
      return assignedEmail === normalisedAdviserEmail;
    });
  }, [allCases, adviserEmail]);

  const getClientNames = (item) => {
    if (Array.isArray(item.clients) && item.clients.length) {
      return item.clients
        .map(
          (client) =>
            client.name ||
            `${client.firstName || ""} ${client.lastName || ""}`.trim(),
        )
        .filter(Boolean)
        .join(" + ");
    }

    return item.client?.name || "-";
  };

  const formatStatusLabel = (status) => {
    if (status === "completed") return "Completed";
    if (status === "cancelled") return "Cancelled";
    if (status === "npw") return "NPW";
    return "In Progress";
  };

  const getStatusClass = (status) => {
    if (status === "completed") return "ParaplanningTableStatus--Completed";
    if (status === "cancelled") return "ParaplanningTableStatus--Cancelled";
    if (status === "npw") return "ParaplanningTableStatus--NPW";
    return "ParaplanningTableStatus--InProgress";
  };

  if (loadingCases) {
    return <div className="ParaplanningTableState">Loading cases...</div>;
  }

  return (
    <div className="ParaplanningAdviserTab">
      {filteredCases.length === 0 ? (
        <div className="ParaplanningTableState">No cases found.</div>
      ) : (
        <div className="ParaplanningTableWrap">
          <table className="ParaplanningTable">
            <thead>
              <tr>
                {[
                  "Start Date",
                  "Client",
                  "Type",
                  "Paraplanner",
                  "Completion Date",
                  "Amount Charged",
                  "Status",
                ].map((label) => (
                  <th key={label}>
                    <span className="ParaplanningTableHeaderButton">
                      <span className="FP_Content">{label}</span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredCases.map((caseItem) => (
                <tr key={caseItem.id}>
                <td>
                  <div className="ParaplanningTableCell">
                    <span className="ParaplanningTableCell__Main FP_Content black">
                      {formatDate(caseItem.startDate || caseItem.createdAt, {
                        showDay: true,
                      })}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="ParaplanningTableCell">
                    <span className="ParaplanningTableCell__Main FP_Content black">
                      {getClientNames(caseItem)}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="ParaplanningTableCell">
                    <span className="ParaplanningTableCell__Main FP_Content black">
                      {caseItem.caseType || caseItem.type || "-"}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="ParaplanningTableCell">
                    <span className="ParaplanningTableCell__Main FP_Content black">
                      {caseItem.assignedTo?.name || "-"}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="ParaplanningTableCell">
                    <span className="ParaplanningTableCell__Main FP_Content black">
                      {caseItem.completionDate
                        ? formatDate(caseItem.completionDate)
                        : "-"}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="ParaplanningTableCell">
                    <span className="ParaplanningTableCell__Main ParaplanningTableCell__Main--Money FP_Content black">
                      {getAmountCharged(caseItem)}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="ParaplanningTableActions">
                    <span
                      className={`ParaplanningTableStatus ${getStatusClass(caseItem.status)}`}
                    >
                      {formatStatusLabel(caseItem.status)}
                    </span>
                  </div>
                </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ParaplanningAdviserTab;
